/* eslint-disable @angular-eslint/directive-selector */
import {
  Directive,
  EmbeddedViewRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

import { Subject, takeUntil } from 'rxjs';

import { UserPermissionService } from '@services/helper/user-permission.service';

@Directive({
  selector: '[hasRole]',
  standalone: true,
})
export class HasRoleDirective implements OnInit, OnDestroy {
  // Reference to the PermissionService, which is used to check if the user has a specific role
  private _permissionService = inject(UserPermissionService);
  // Reference to the TemplateRef, which is used to get a reference to the template element
  private _templateRef = inject(TemplateRef<any>);
  // Reference to the ViewContainerRef, which is used to create or destroy embedded views
  private _viewContainer = inject(ViewContainerRef);

  // Subject to notify when the component has been destroyed
  private _destroy$ = new Subject<void>();
  // Array of roles to check against
  private _roles: string[] = [];
  // Reference to the embedded view, which is created or destroyed based on the user's roles
  private _viewRef: EmbeddedViewRef<any> | null = null;

  @Input() public set hasRole(value: string | string[]) {
    // ✅ Normalize input
    if (Array.isArray(value)) {
      this._roles = value.filter((v) => !!v);
    } else if (typeof value === 'string' && value.trim()) {
      this._roles = [value.trim()];
    } else {
      this._roles = []; // empty string / empty array → allow
    }

    this.updateView();
  }

  public ngOnInit(): void {
    // Subscribe to current role and update view when it changes
    this._permissionService
      .getCurrentRole$()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this.updateView());
  }

  public ngOnDestroy(): void {
    // Call next on the destroy$ Subject to indicate that the component has been destroyed,
    // and then call complete to mark the subject as complete.
    // This is done to ensure that any subscriptions to the destroy$ Subject are cleaned up.
    this._destroy$.next();
    this._destroy$.complete();
  }

  private updateView(): void {
    // Check if user has required permissions
    const hasAccess = this.checkRoles();

    // If user has required permissions and the view hasn't been created yet,
    // create the view.
    if (hasAccess && !this._viewRef) {
      this._viewContainer.clear(); // hydration-safe
      this._viewRef = this._viewContainer.createEmbeddedView(this._templateRef);
    }

    // If user doesn't have required permissions and the view has been created,
    // destroy the view.
    if (!hasAccess && this._viewRef) {
      this._viewContainer.clear();
      this._viewRef = null;
    }
  }

  private checkRoles(): boolean {
    // ✅ Empty input → allow
    if (this._roles.length === 0) {
      return true;
    }

    // Get current user role
    const currentRole = this._permissionService.getCurrentRole();

    // If no current role is found, deny access
    if (!currentRole) {
      return false;
    }
    // Check if current role is included in the required roles
    // If it is, grant access. Otherwise, deny access.
    return this._roles.includes(currentRole);
  }
}

@Directive({
  selector: '[hasPermission]',
  standalone: true,
})
export class HasPermissionDirective implements OnInit, OnDestroy {
  // Reference to the permission service
  // This is used to check if the user has the required permissions
  private _permissionService = inject(UserPermissionService);

  // Reference to the template ref
  // This is used to create an embedded view if the user has the required permissions
  private _templateRef = inject(TemplateRef<any>);

  // Reference to the view container
  // This is used to create an embedded view if the user has the required permissions
  private _viewContainer = inject(ViewContainerRef);

  // Subject to notify when the component is destroyed
  // and the change detector is no longer being checked
  // It is a good idea to do all component teardown logic here
  // to allow Angular to free up memory occupied by the component
  private _destroy$ = new Subject<void>();

  // Permissions required for the content to be visible
  // If the permission is an array, all permissions in the array must be present
  // If the permission is a string, only that permission must be present
  private _permissions: string[] = [];

  // Reference to the embedded view that is created if the user has the required permissions
  // If the user does not have the required permissions, this is null
  private _viewRef: EmbeddedViewRef<any> | null = null;

  /**
   * Sets the permissions required for the content to be visible.
   * If the permission is an array, all permissions in the array must be present.
   * If the permission is a string, only that permission must be present.
   */
  @Input() public set hasPermission(permissions: string | string[]) {
    this._permissions = Array.isArray(permissions) ? permissions : [permissions];
    this.updateView();
  }
  /**
   * Whether the user must have all permissions to access the content.
   * If true, user must have all permissions.
   * If false, user only needs to have one of the permissions.
   */
  @Input() public hasPermissionRequireAll = false;

  // Subscribe to current permissions and update view when they change
  public ngOnInit(): void {
    this._permissionService
      .getCurrentPermissions$()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this.updateView());
  }
  /**
   * Clean up resources when component is destroyed
   * and the change detector is no longer being checked
   * It is a good idea to do all component teardown logic here
   * to allow Angular to free up memory occupied by the component
   */
  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private updateView(): void {
    // Check if user has required permissions
    // If hasPermissionRequireAll is true, user must have all permissions
    // Otherwise, user only needs to have one of the permissions
    const hasPermission = this.checkPermissions();
    // If user has required permissions and the view hasn't been created yet,
    // create the view.
    if (hasPermission && !this._viewRef) {
      this._viewContainer.clear(); // 🔑 hydration-safe
      this._viewRef = this._viewContainer.createEmbeddedView(this._templateRef);
    }
    // If user doesn't have required permissions and the view has been created,
    // destroy the view.
    if (!hasPermission && this._viewRef) {
      this._viewContainer.clear();
      this._viewRef = null;
    }
  }

  private checkPermissions(): boolean {
    // If no permissions are defined, allow access
    if (this._permissions.length === 0) {
      return true;
    }

    // Check if user has all required permissions
    // If hasPermissionRequireAll is true, user must have all permissions
    // Otherwise, user only needs to have one of the permissions
    return this.hasPermissionRequireAll
      ? this._permissions.every((p) => this._permissionService.hasPermission(p))
      : this._permissions.some((p) => this._permissionService.hasPermission(p));
  }
}
